"use client";

import { useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { LogIn, LogOut, X } from "lucide-react";
import { useLang, type Lang } from "@/src/i18n";
import {
  AuthError,
  completeNewPassword,
  confirmForgotPassword,
  confirmSignUp,
  currentUserEmail,
  forgotPassword,
  getAuthConfig,
  onAuthChange,
  resendCode,
  signIn,
  signOut,
  signUp,
} from "@/src/services/auth";

/**
 * Sign-in UI. Renders nothing when /config.js has no `auth` block.
 *  - <AccountButton/> sits in the top bar: "Sign in" or the signed-in email.
 *  - <AuthGate/> wraps the app and blocks it only when auth.loginRequired is
 *    true (after the open demo link is switched off).
 */

type Mode = "signIn" | "signUp" | "confirm" | "newPassword" | "forgot" | "reset";

const S: Record<string, Record<Lang, string>> = {
  signIn: { en: "Sign in", th: "เข้าสู่ระบบ" },
  signOut: { en: "Sign out", th: "ออกจากระบบ" },
  signUp: { en: "Create account", th: "สร้างบัญชี" },
  email: { en: "Email", th: "อีเมล" },
  password: { en: "Password", th: "รหัสผ่าน" },
  newPassword: { en: "New password", th: "รหัสผ่านใหม่" },
  code: { en: "Verification code", th: "รหัสยืนยัน" },
  confirm: { en: "Verify email", th: "ยืนยันอีเมล" },
  forgot: { en: "Forgot password?", th: "ลืมรหัสผ่าน?" },
  sendCode: { en: "Send reset code", th: "ส่งรหัสรีเซ็ต" },
  reset: { en: "Set new password", th: "ตั้งรหัสผ่านใหม่" },
  resend: { en: "Send the code again", th: "ส่งรหัสอีกครั้ง" },
  back: { en: "Back to sign in", th: "กลับไปหน้าเข้าสู่ระบบ" },
  noAccount: { en: "SATI staff without an account?", th: "พนักงาน SATI ที่ยังไม่มีบัญชี?" },
  haveAccount: { en: "Already have an account?", th: "มีบัญชีอยู่แล้ว?" },
  close: { en: "Close", th: "ปิด" },
  wait: { en: "Please wait…", th: "กรุณารอสักครู่…" },
  title: { en: "Sign in to Progress Note AI", th: "เข้าสู่ระบบ Progress Note AI" },
  hintSignUp: {
    en: "Use your @sati.co.th email. We will email you a code to confirm it.",
    th: "ใช้อีเมล @sati.co.th ระบบจะส่งรหัสยืนยันไปที่อีเมลของคุณ",
  },
  hintInvite: {
    en: "Not SATI staff? Ask a SATI admin for an invite.",
    th: "ไม่ใช่พนักงาน SATI? ขอคำเชิญจากผู้ดูแลระบบ SATI",
  },
  hintConfirm: { en: "Enter the code we emailed to", th: "กรอกรหัสที่ส่งไปที่" },
  hintNewPassword: {
    en: "Welcome. Please choose your own password to finish signing in.",
    th: "ยินดีต้อนรับ กรุณาตั้งรหัสผ่านของคุณเองเพื่อเข้าสู่ระบบ",
  },
  hintReset: { en: "Enter the code we emailed and a new password.", th: "กรอกรหัสที่ส่งทางอีเมลและรหัสผ่านใหม่" },
  passwordRule: { en: "At least 10 characters, with a lowercase letter and a number.", th: "อย่างน้อย 10 ตัวอักษร มีตัวพิมพ์เล็กและตัวเลข" },
  sentCode: { en: "A new code is on its way.", th: "ส่งรหัสใหม่แล้ว" },
  verified: { en: "Email verified. Please sign in.", th: "ยืนยันอีเมลแล้ว กรุณาเข้าสู่ระบบ" },
  resetDone: { en: "Password changed. Please sign in.", th: "เปลี่ยนรหัสผ่านแล้ว กรุณาเข้าสู่ระบบ" },
};

/** Plain-language messages for the Cognito errors people actually hit. */
function friendlyError(err: unknown, lang: Lang): string {
  const code = err instanceof AuthError ? err.code : "";
  const th = lang === "th";
  switch (code) {
    case "NotAuthorizedException":
      return th ? "อีเมลหรือรหัสผ่านไม่ถูกต้อง" : "Wrong email or password.";
    case "UserNotConfirmedException":
      return th ? "ยังไม่ได้ยืนยันอีเมล" : "Your email isn't verified yet.";
    case "UsernameExistsException":
      return th ? "อีเมลนี้มีบัญชีแล้ว กรุณาเข้าสู่ระบบ" : "This email already has an account. Please sign in.";
    case "CodeMismatchException":
      return th ? "รหัสไม่ถูกต้อง" : "That code is not right.";
    case "ExpiredCodeException":
      return th ? "รหัสหมดอายุ กรุณาขอรหัสใหม่" : "That code has expired. Ask for a new one.";
    case "InvalidPasswordException":
      return S.passwordRule[lang];
    case "LimitExceededException":
    case "TooManyRequestsException":
      return th ? "พยายามหลายครั้งเกินไป กรุณารอสักครู่" : "Too many attempts. Please wait a few minutes.";
    case "UserLambdaValidationException": {
      // The sign-up domain rule; Cognito prefixes its own text.
      return th
        ? "กรุณาใช้อีเมล @sati.co.th หรือขอคำเชิญจากผู้ดูแลระบบ"
        : "Please use your @sati.co.th email, or ask an admin for an invite.";
    }
    default:
      return err instanceof Error && err.message ? err.message : th ? "เกิดข้อผิดพลาด กรุณาลองใหม่" : "Something went wrong. Please try again.";
  }
}

function useSignedInEmail(): string | null {
  // Subscribes to sign-in/out; the server snapshot is "signed out".
  return useSyncExternalStore(onAuthChange, currentUserEmail, () => null);
}

/** Auth config is static for the page's lifetime (read from /config.js). */
function useAuthConfig() {
  return useSyncExternalStore(noopSubscribe, getAuthConfig, () => null);
}
const noopSubscribe = () => () => {};

function SignInForm({ onDone }: { onDone?: () => void }) {
  const { lang } = useLang();
  const [mode, setMode] = useState<Mode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const s = (k: string) => S[k][lang];

  const go = (next: Mode, message = "") => {
    setMode(next);
    setError("");
    setNotice(message);
    setCode("");
    if (next !== "confirm") setPassword("");
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (mode === "signIn") {
        const out = await signIn(email, password);
        if (out.status === "newPasswordRequired") {
          setChallenge(out.session);
          go("newPassword");
        } else onDone?.();
      } else if (mode === "newPassword") {
        await completeNewPassword(email, password, challenge);
        onDone?.();
      } else if (mode === "signUp") {
        await signUp(email, password);
        go("confirm");
      } else if (mode === "confirm") {
        await confirmSignUp(email, code);
        // Sign straight in when we still have the password from sign-up.
        if (password) {
          await signIn(email, password);
          onDone?.();
        } else go("signIn", s("verified"));
      } else if (mode === "forgot") {
        await forgotPassword(email);
        go("reset");
      } else if (mode === "reset") {
        await confirmForgotPassword(email, code, password);
        go("signIn", s("resetDone"));
      }
    } catch (err) {
      if (err instanceof AuthError && err.code === "UserNotConfirmedException") {
        await resendCode(email).catch(() => undefined);
        go("confirm");
      } else setError(friendlyError(err, lang));
    } finally {
      setBusy(false);
    }
  }

  const needsPassword = mode === "signIn" || mode === "signUp" || mode === "newPassword" || mode === "reset";
  const needsCode = mode === "confirm" || mode === "reset";
  const needsEmail = mode === "signIn" || mode === "signUp" || mode === "forgot";
  const submitLabel = {
    signIn: s("signIn"), signUp: s("signUp"), confirm: s("confirm"),
    newPassword: s("reset"), forgot: s("sendCode"), reset: s("reset"),
  }[mode];

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      {mode === "signUp" && <p className="auth-hint">{s("hintSignUp")}</p>}
      {mode === "confirm" && <p className="auth-hint">{s("hintConfirm")} <strong>{email}</strong></p>}
      {mode === "newPassword" && <p className="auth-hint">{s("hintNewPassword")}</p>}
      {mode === "reset" && <p className="auth-hint">{s("hintReset")}</p>}
      {notice && <p className="auth-notice" role="status">{notice}</p>}

      {needsEmail && (
        <label>
          <span>{s("email")}</span>
          <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
      )}
      {needsCode && (
        <label>
          <span>{s("code")}</span>
          <input inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
        </label>
      )}
      {needsPassword && (
        <label>
          <span>{mode === "signIn" ? s("password") : s("newPassword")}</span>
          <input
            type="password"
            autoComplete={mode === "signIn" ? "current-password" : "new-password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-describedby={mode === "signIn" ? undefined : "auth-password-rule"}
          />
          {mode !== "signIn" && <small id="auth-password-rule">{s("passwordRule")}</small>}
        </label>
      )}

      {error && <p className="auth-error" role="alert">{error}</p>}
      <button type="submit" className="auth-submit" disabled={busy}>{busy ? s("wait") : submitLabel}</button>

      <div className="auth-links">
        {mode === "signIn" && (
          <>
            <button type="button" onClick={() => go("forgot")}>{s("forgot")}</button>
            <span>{s("noAccount")} <button type="button" onClick={() => go("signUp")}>{s("signUp")}</button></span>
            <span className="auth-muted">{s("hintInvite")}</span>
          </>
        )}
        {mode === "signUp" && (
          <span>{s("haveAccount")} <button type="button" onClick={() => go("signIn")}>{s("signIn")}</button></span>
        )}
        {mode === "confirm" && (
          <button type="button" onClick={async () => { await resendCode(email).catch(() => undefined); setNotice(s("sentCode")); }}>{s("resend")}</button>
        )}
        {(mode === "forgot" || mode === "reset" || mode === "confirm" || mode === "newPassword") && (
          <button type="button" onClick={() => go("signIn")}>{s("back")}</button>
        )}
      </div>
    </form>
  );
}

/** Top-bar control: "Sign in" button, or the signed-in email with sign out. */
export function AccountButton() {
  const { lang } = useLang();
  const email = useSignedInEmail();
  const [open, setOpen] = useState(false);
  const enabled = Boolean(useAuthConfig());
  if (!enabled) return null;

  if (email) {
    return (
      <div className="auth-account">
        <span className="auth-email" title={email}>{email}</span>
        <button type="button" className="auth-chip" onClick={() => void signOut()} aria-label={S.signOut[lang]} title={S.signOut[lang]}>
          <LogOut size={15} aria-hidden="true" />
        </button>
      </div>
    );
  }
  return (
    <>
      <button type="button" className="auth-chip auth-chip-primary" onClick={() => setOpen(true)}>
        <LogIn size={15} aria-hidden="true" /> {S.signIn[lang]}
      </button>
      {open && (
        <div className="auth-backdrop" role="presentation" onClick={() => setOpen(false)}>
          <div className="auth-card" role="dialog" aria-modal="true" aria-labelledby="auth-title" onClick={(e) => e.stopPropagation()}>
            <div className="auth-card-head">
              <h2 id="auth-title">{S.title[lang]}</h2>
              <button type="button" className="auth-close" onClick={() => setOpen(false)} aria-label={S.close[lang]}><X size={18} /></button>
            </div>
            <SignInForm onDone={() => setOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}

/** Blocks the app behind sign-in only when config.js sets auth.loginRequired. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { lang } = useLang();
  const email = useSignedInEmail();
  const required = Boolean(useAuthConfig()?.loginRequired);
  if (!required || email) return <>{children}</>;
  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="auth-card-head auth-brand">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/sati-logo.png" alt="Sati" width="40" height="40" />
          <h1 id="auth-title">{S.title[lang]}</h1>
        </div>
        <SignInForm />
      </div>
    </main>
  );
}
